import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Col, Empty, Row, Segmented, Spin, Statistic } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  MinusCircleOutlined,
  CarOutlined,
  FieldTimeOutlined,
  InboxOutlined,
} from '@ant-design/icons';
import { Pie, Column } from '@ant-design/charts';
import * as partnerApi from '../modules/partners/api';
import './PartnerStats.scss';

// PERIOD_OPTIONS moved inside component to use translations

const STATUS_COLORS = {
  COMPLETED: '#2e7d32',
  DELIVERY_FAILED: '#c62828',
  SKIPPED: '#f57c00',
  ASSIGNED: '#1565c0',
  UNASSIGNED: '#9e9e9e',
  DISPATCHED: '#4527a0',
  COLLECTED: '#6a1b9a',
  PICKUP_ARRIVED: '#6a1b9a',
  DELIVERY_ARRIVED: '#6a1b9a',
  SERVICE_ARRIVED: '#6a1b9a',
  OPTIMIZED: '#00838f',
  ERROR: '#b71c1c',
  DRAFT: '#bdbdbd',
};

const CATEGORY_COLORS = {
  PRODUCT: '#1565c0',
  EMPTY: '#f57c00',
  UNKNOWN: '#9e9e9e',
};

const formatMins = (mins) => {
  if (!mins || mins <= 0) return '0m';
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const PartnerStats = ({ partnerId, viewOnly }) => {
  const { t } = useTranslation('stats');

  const PERIOD_OPTIONS = [
    { label: t('periods.today'), value: 'today' },
    { label: t('periods.this_week'), value: 'this_week' },
    { label: t('periods.last_week'), value: 'last_week' },
    { label: t('periods.last_month'), value: 'last_month' },
    { label: t('periods.all'), value: 'all' },
  ];
  const [period, setPeriod] = useState('this_week');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    const { response } = viewOnly
      ? await partnerApi.getMyPartnerStats(period)
      : await partnerApi.getPartnerStats(partnerId, period);
    if (response) setStats(response);
    setLoading(false);
  }, [partnerId, period, viewOnly]);

  useEffect(() => {
    if (partnerId || viewOnly) fetchStats();
  }, [fetchStats, partnerId, viewOnly]);

  if (loading && !stats) {
    return (
      <div className="partner-stats">
        <Spin style={{ display: 'block', paddingTop: 80 }} />
      </div>
    );
  }

  if (!stats || stats.delivery.total === 0) {
    return (
      <div className="partner-stats">
        <Segmented options={PERIOD_OPTIONS} value={period} onChange={setPeriod} className="period-selector" />
        {loading ? (
          <Spin style={{ display: 'block', paddingTop: 80 }} />
        ) : (
          <Empty description={t('noDataForPeriod')} style={{ paddingTop: 60 }} />
        )}
      </div>
    );
  }

  // Chart data
  const statusData = Object.entries(stats.time.ordersByStatus).map(([status, value]) => ({
    type: status.replace(/_/g, ' '),
    value,
    color: STATUS_COLORS[status] || '#9e9e9e',
  }));

  const onTimeData = [
    { type: 'On time', value: stats.time.onTimeCount, color: '#2e7d32' },
    { type: 'Late', value: stats.time.lateCount, color: '#c62828' },
  ].filter((d) => d.value > 0);

  const categoryData = Object.entries(stats.collection.byCategory).map(([cat, value]) => ({
    type: cat,
    value,
    color: CATEGORY_COLORS[cat] || '#9e9e9e',
  }));

  const dowOrder = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const dowData = dowOrder.map((day) => ({
    day,
    orders: stats.time.ordersByDayOfWeek[day] || 0,
  }));

  const pieConfig = {
    height: 240,
    innerRadius: 0.6,
    radius: 0.85,
    label: { text: 'value', style: { fontWeight: 600 } },
    legend: { color: { position: 'bottom', layout: { justifyContent: 'center' } } },
    tooltip: { title: 'type' },
    style: { lineWidth: 2, stroke: '#fff' },
  };

  return (
    <div className="partner-stats">
      <Segmented options={PERIOD_OPTIONS} value={period} onChange={setPeriod} className="period-selector" />

      <Spin spinning={loading}>
        {/* Delivery Performance */}
        <div className="stats-section">
          <div className="section-title">Delivery Performance</div>
          <Row gutter={[16, 16]}>
            <Col xs={12} sm={8} md={4}>
              <Card className="stat-card">
                <Statistic title="Total" value={stats.delivery.total} valueStyle={{ color: '#212121' }} />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Card className="stat-card success">
                <Statistic
                  title="Completed"
                  value={stats.delivery.completed}
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ color: '#2e7d32' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Card className="stat-card danger">
                <Statistic
                  title="Failed"
                  value={stats.delivery.failed}
                  prefix={<CloseCircleOutlined />}
                  valueStyle={{ color: '#c62828' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Card className="stat-card warning">
                <Statistic
                  title="Skipped"
                  value={stats.delivery.skipped}
                  prefix={<MinusCircleOutlined />}
                  valueStyle={{ color: '#f57c00' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Card className="stat-card">
                <Statistic title="Completion Rate" value={stats.delivery.completionRate} suffix="%" precision={1} />
              </Card>
            </Col>
          </Row>
        </div>

        {/* Charts Row 1: Status + On-Time */}
        <Row gutter={[16, 16]} className="stats-section">
          <Col xs={24} md={12}>
            <Card className="chart-card" title="Orders by Status">
              {statusData.length > 0 ? (
                <Pie
                  data={statusData}
                  angleField="value"
                  colorField="type"
                  color={statusData.map((d) => d.color)}
                  {...pieConfig}
                />
              ) : (
                <Empty description="No data" />
              )}
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card className="chart-card" title="On-Time Performance">
              {onTimeData.length > 0 ? (
                <Pie
                  data={onTimeData}
                  angleField="value"
                  colorField="type"
                  color={['#2e7d32', '#c62828']}
                  {...pieConfig}
                />
              ) : (
                <Empty description="No delivery time data" />
              )}
            </Card>
          </Col>
        </Row>

        {/* Route Operations */}
        <div className="stats-section">
          <div className="section-title">Route Operations</div>
          <Row gutter={[16, 16]}>
            <Col xs={12} sm={6}>
              <Card className="stat-card">
                <Statistic title="Routes" value={stats.routes.total} prefix={<CarOutlined />} />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card className="stat-card">
                <Statistic title="Avg Stops / Route" value={stats.routes.avgStopsPerRoute} precision={1} />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card className="stat-card">
                <Statistic title="Total Distance" value={stats.routes.totalDistanceKm} suffix="km" precision={1} />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card className="stat-card">
                <Statistic
                  title="Avg Duration"
                  value={formatMins(stats.routes.avgDurationMins)}
                  prefix={<FieldTimeOutlined />}
                  formatter={(val) => val}
                />
              </Card>
            </Col>
          </Row>
        </div>

        {/* Charts Row 2: Items + Day of Week */}
        <Row gutter={[16, 16]} className="stats-section">
          <Col xs={24} md={12}>
            <Card className="chart-card" title="Items by Category">
              {categoryData.length > 0 ? (
                <Pie
                  data={categoryData}
                  angleField="value"
                  colorField="type"
                  color={categoryData.map((d) => d.color)}
                  {...pieConfig}
                />
              ) : (
                <Empty description="No items collected" />
              )}
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card className="chart-card" title="Orders by Day of Week">
              <Column
                data={dowData}
                xField="day"
                yField="orders"
                height={240}
                color="#212121"
                label={{ position: 'top', style: { fontWeight: 600 } }}
                xAxis={{ label: { style: { fontSize: 12 } } }}
              />
            </Card>
          </Col>
        </Row>

        {/* Collection & Time */}
        <div className="stats-section">
          <div className="section-title">Collection & Time</div>
          <Row gutter={[16, 16]}>
            <Col xs={12} sm={6}>
              <Card className="stat-card">
                <Statistic title="Items Collected" value={stats.collection.totalItems} prefix={<InboxOutlined />} />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card className="stat-card">
                <Statistic title="Items / Route" value={stats.collection.avgItemsPerRoute} precision={1} />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card className="stat-card">
                <Statistic
                  title="Avg Time / Stop"
                  value={formatMins(stats.time.avgTimePerStopMins)}
                  formatter={(val) => val}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card className="stat-card">
                <Statistic
                  title="Return Drop-off"
                  value={stats.collection.returnDropoffRate}
                  suffix="%"
                  precision={1}
                />
              </Card>
            </Col>
          </Row>
        </div>
      </Spin>
    </div>
  );
};

export default PartnerStats;
